import DeliverySDK from '@olxeu-a2b/core/src/api/DeliverySDK';
import { Dispatch, Reducer } from 'react';
import { sdkFactory } from '../extras/sdkFactory';
import useReducer from '../extras/useReducer';

export interface FluxAction<T> {
  type: string;
  payload?: T;
}
export type UseSDKHook<CallerParams, ReturningEntity> =
  | readonly [(params?: CallerParams) => void, null, true, null]
  | readonly [(params?: CallerParams) => void, ReturningEntity, false, null]
  | readonly [(params?: CallerParams) => void, null, false, Error];

export type CallState<T> =
  | {
      response: T;
      loading: false;
      error: null;
    }
  | {
      response: null;
      loading: true;
      error: null;
    }
  | {
      response: null;
      loading: false;
      error: Error;
    };

const callStateFor = <T>(): CallState<T> => ({
  response: null,
  loading: false,
  error: null,
});

/**
 * Actions
 */
const API_CALL_SUCCESS = 'API_CALL_SUCCESS' as const;
const API_CALL_LOADING = 'API_CALL_LOADING' as const;
const API_CALL_ERROR = 'API_CALL_ERROR' as const;

const callReducerFor =
  <T>(): Reducer<CallState<T>, FluxAction<any>> =>
    (state, { type, payload }): CallState<T> => {
      switch (type) {
      case API_CALL_SUCCESS:
        return { ...state, response: payload, loading: false };

      case API_CALL_LOADING:
        return { ...state, loading: payload, error: undefined };

      case API_CALL_ERROR:
        return { ...state, error: payload, loading: false };

      default:
        return state;
      }
    };

const apiCallLoading = (dispatch: Dispatch<FluxAction<boolean>>) => (payload: boolean) =>
  dispatch({ type: API_CALL_LOADING, payload });

const apiCallError = (dispatch: Dispatch<FluxAction<Error>>) => (payload: Error) =>
  dispatch({ type: API_CALL_ERROR, payload });

const apiCallSuccessFor =
  <Response>() =>
    (dispatch: Dispatch<FluxAction<Response>>) =>
      (payload: Response) =>
        dispatch({ type: API_CALL_SUCCESS, payload });

export default <
  Caller extends (sdk: DeliverySDK, params?: any) => Promise<any>,
  Params = Caller extends (sdk: DeliverySDK, params?: infer P) => any ? P : never,
  Response = Caller extends (sdk: DeliverySDK, params?: any) => Promise<infer R> ? R : never,
>(
  caller: Caller,
): UseSDKHook<Params, Response> => {
  const [state, dispatch] = useReducer(callReducerFor<Response>(), callStateFor<Response>());
  const successAction = apiCallSuccessFor<Response>()(dispatch);
  const loadingAction = apiCallLoading(dispatch);
  const errorAction = apiCallError(dispatch);
  const { response, error, loading } = state;
  const call = (params?: Params): void => {
    loadingAction(true);
    Promise.resolve(sdkFactory())
      .then(sdk => caller(sdk, params))
      .then(res => successAction(res))
      .catch(err => errorAction(err));
  };
  if (loading) {
    return [call, null, true, null] as const;
  }
  if (response) {
    return [call, response, false, null] as const;
  }
  return [call, null, false, error] as const;
};
